import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/useAuth";
import { useSidebarState } from "@/hooks/useSidebarState";
import { navigationConfig } from "@/config/navigation";
import { SidebarCategory } from "./SidebarCategory";
import { SidebarChildItem } from "./SidebarChildItem";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from "@/components/ui/sidebar";

export function AppSidebar() {
  const { isAdmin, isSuperuser, user } = useAuth();
  const location = useLocation();
  const { state, setOpenMobile, isMobile } = useSidebar();
  const collapsed = state === "collapsed";
  const { toggleCategory, isExpanded, isChildActive } = useSidebarState();

  useEffect(() => {
    if (isMobile) {
      setOpenMobile(false);
    }
  }, [location.pathname, isMobile, setOpenMobile]);

  const canShowAdminItems = isAdmin || isSuperuser;

  return (
    <Sidebar
      className="border-r-0"
      style={{
        "--sidebar-background": "hsl(var(--sidebar-bg))",
      } as React.CSSProperties}
    >
      {/* Brand Header */}
      <SidebarHeader className="p-4 border-b border-[hsl(var(--sidebar-border))] bg-[hsl(var(--sidebar-bg))]">
        <Link to="/dashboard" className="flex items-center gap-2">
          {collapsed ? (
            <img src="/icon-mark.png" alt="Doyence" className="h-6 w-6" />
          ) : (
            <div className="flex items-center gap-2">
              <img src="/icon-mark.png" alt="Doyence" className="h-6 w-6" />
              <span className="font-bold text-xl text-white">
                Doyence
              </span>
            </div>
          )}
        </Link>
      </SidebarHeader>

      <SidebarContent className="p-2 bg-[hsl(var(--sidebar-bg))]">
        <SidebarMenu>
          {navigationConfig.map((item) => {
            // Skip admin items if user doesn't have permission
            if (item.adminOnly && !canShowAdminItems) {
              return null;
            }

            // Hide customer-facing nav for non-impersonating superusers
            if (
              isSuperuser &&
              !user?.impersonated &&
              item.id !== 'dashboard' &&
              item.id !== 'admin'
            ) {
              return null;
            }

            // Direct navigation item (Dashboard, Admin)
            if (item.href) {
              const isActive = location.pathname === item.href || 
                location.pathname.startsWith(item.href + "/");
              const Icon = item.icon;

              return (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive}
                    className={cn(
                      "text-[hsl(var(--sidebar-text))] hover:bg-[hsl(var(--sidebar-bg-hover))]",
                      isActive && [
                        "bg-[hsl(var(--sidebar-accent-tint))]",
                        "border-l-3 border-[hsl(var(--sidebar-accent))]",
                      ]
                    )}
                  >
                    <Link to={item.href} className="flex items-center gap-2">
                      <Icon className="h-4 w-4" />
                      {!collapsed && <span className="font-medium">{item.label}</span>}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            }

            // Expandable category
            if (item.children) {
              const categoryExpanded = isExpanded(item.id);
              const hasActiveChild = item.children.some((child) => isChildActive(child.href));

              return (
                <SidebarMenuItem key={item.id} className="flex flex-col">
                  <SidebarCategory
                    item={item}
                    isExpanded={categoryExpanded}
                    hasActiveChild={hasActiveChild}
                    onToggle={() => toggleCategory(item.id)}
                    collapsed={collapsed}
                  />
                  
                  {/* Children - only show when expanded and not collapsed */}
                  {categoryExpanded && !collapsed && (
                    <div className="mt-1 space-y-0.5">
                      {item.children.map((child) => (
                        <SidebarChildItem
                          key={child.id}
                          item={child}
                          isActive={isChildActive(child.href)}
                        />
                      ))}
                    </div>
                  )}
                </SidebarMenuItem>
              );
            }

            return null;
          })}
        </SidebarMenu>
      </SidebarContent>
      {isSuperuser && !user?.impersonated && (
        <SidebarFooter className="p-2 border-t border-[hsl(var(--sidebar-border))] bg-[hsl(var(--sidebar-bg))]">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                className="text-[hsl(var(--sidebar-text))] hover:bg-[hsl(var(--sidebar-bg-hover))]"
              >
                <a
                  href="https://admin.doyencegroup.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  {!collapsed && <span className="font-medium">Superuser Portal</span>}
                </a>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      )}
    </Sidebar>
  );
}

export default AppSidebar;
